'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Paintbrush } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { getNodeEditorConfig } from '@/components/builder/node-editor-registry';
import { useFunnelCreation, type CreatedFunnel } from '@/components/funnels/useFunnelCreation';
import { SETUP_MOTION } from '@/components/setup/experience/constants';
import { SetupCheckoutPreviewPanel } from '@/components/setup/experience/SetupCheckoutPreviewPanel';
import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import { SetupStepSection } from '@/components/setup/experience/SetupStepSection';
import { SetupCheckoutGeneratingPanel } from '@/components/setup/experience/steps/SetupCheckoutGeneratingPanel';
import { SetupCheckoutUpsellOfferStep } from '@/components/setup/experience/steps/SetupCheckoutUpsellOfferStep';
import { SetupCheckoutUpsellStep } from '@/components/setup/experience/steps/SetupCheckoutUpsellStep';
import { SetupCheckoutVerticalStep } from '@/components/setup/experience/steps/SetupCheckoutVerticalStep';
import {
  buildSetupProductsListInput,
  SETUP_CATALOG_LIST_PAGE_SIZE,
} from '@/components/setup/experience/lib/setupCatalogSummary';
import { buildCheckoutProductOptions } from '@/components/setup/experience/lib/setupCheckoutLineItem';
import { extractCheckoutUrlFromFunnel } from '@/components/setup/experience/lib/setupCheckoutUtils';
import {
  getSetupFunnelStarterOverrides,
  getSetupFunnelTemplateId,
  getSetupFunnelTheme,
  getSetupFunnelVerticalOptions,
  type SetupFunnelVertical,
} from '@/components/setup/experience/lib/setupFunnelGeneration';
import type { SetupSourcePath } from '@/components/setup/experience/types';
import { useFunnel, useUpdateFunnelWithRouting } from '@/lib/api/funnels';
import { useProductsList } from '@/lib/api/products';
import { useLatestStoreTask, type StoreTaskType } from '@/lib/api/tasks';
import { trpc } from '@/lib/api/trpc';
import { useUpdateStoreVertical } from '@/lib/api/stores';

type SetupCheckoutPhase = 'vertical' | 'upsell' | 'upsell-offer' | 'generating' | 'preview';

/** Poll cadence for the catalog while a platform sync is importing products. */
const SETUP_PRODUCT_POLL_INTERVAL_MS = 2500;

/** Connected platform → the background sync task that imports its catalog. */
const PROVIDER_SYNC_TASK_TYPE = {
  shopify: 'shopify_product_sync',
  woocommerce: 'woocommerce_product_sync',
} as const satisfies Record<'shopify' | 'woocommerce', StoreTaskType>;

/** Shape of an offer node carrying a bound upsell offer resource. */
type FunnelWithOfferNode = {
  config?: {
    nodes?: Array<{
      type?: string;
      config?: {
        stepConfig?: {
          resources?: Record<string, unknown>;
          staticResources?: Record<string, unknown>;
        };
      };
    }>;
  };
};

/** True once the funnel's offer step is bound to every expected upsell offer id. */
function offerNodeHasOfferBinding(funnel: unknown, expectedOfferIds: string[]): boolean {
  if (expectedOfferIds.length === 0) return false;
  const nodes = (funnel as FunnelWithOfferNode | undefined)?.config?.nodes ?? [];
  const offerNode = nodes.find((node) => node?.type === 'offer');
  if (!offerNode) return false;
  const stepConfig = offerNode.config?.stepConfig;
  const bound = stepConfig?.resources?.offer ?? stepConfig?.staticResources?.offer;
  const boundList = Array.isArray(bound) ? bound : typeof bound === 'string' ? [bound] : [];
  return expectedOfferIds.every((id) => boundList.includes(id));
}

interface SetupCheckoutStepProps {
  storeId: string;
  baseCurrency: string;
  storeLogoUrl?: string | null;
  storeName?: string;
  /** Store brand color (onboarding step 1) — baked into the generated checkout's palette tokens. */
  storePrimaryColor?: string | null;
  /** Funnel already launched for this store, derived from the DB (null until the first launch). */
  launchedFunnelId: string | null;
  /** Connected platform (DB-derived) — drives which background product sync to await; null for manual. */
  connectedProvider: SetupSourcePath | null;
  /** Digital store (step-1 toggle) — hides physical verticals from the "what do you sell?" question. */
  isDigital: boolean;
  /** Pre-selected niche from the DB — restores the merchant's previous answer on re-entry. */
  initialVertical: SetupFunnelVertical | null;
  onBack: () => void;
  /** A funnel was just launched — refresh the DB-derived progress. */
  onLaunched: () => void;
  onComplete: () => void;
}

const CHECKOUT_PHASE_MOTION = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: SETUP_MOTION.transition,
} as const;

/**
 * Final setup step — guided funnel generation. The merchant answers what they
 * sell (Q1) and whether to add a one-click upsell (Q2); those answers derive a
 * funnel template + per-step studio starter overrides that the studio
 * auto-deploy pipeline publishes into a live checkout → [offer] → thank-you
 * funnel. Replaces the previous manual template-grid + native launch.
 */
export function SetupCheckoutStep({
  storeId,
  baseCurrency,
  storeLogoUrl,
  storeName,
  storePrimaryColor,
  launchedFunnelId,
  connectedProvider,
  isDigital,
  initialVertical,
  onBack,
  onLaunched,
  onComplete,
}: SetupCheckoutStepProps) {
  const { t } = useTranslation('setup');
  const utils = trpc.useUtils();

  const [phase, setPhase] = React.useState<SetupCheckoutPhase>(launchedFunnelId ? 'preview' : 'vertical');
  // Pre-select the persisted niche, but drop it if it no longer matches the store kind (e.g. the
  // step-1 digital toggle was flipped after it was chosen) — otherwise we'd seed an option that's
  // filtered out of the list yet still leaves "Continue" enabled.
  const [vertical, setVertical] = React.useState<SetupFunnelVertical | null>(() =>
    initialVertical && getSetupFunnelVerticalOptions(isDigital).some((option) => option.id === initialVertical)
      ? initialVertical
      : null,
  );
  const [hasUpsell, setHasUpsell] = React.useState<boolean | null>(null);
  // Offer chosen/created for the one-click upsell (only when hasUpsell === true).
  const [upsellOfferIds, setUpsellOfferIds] = React.useState<string[]>([]);
  // Answer signature of the funnel already generated this session — lets the user
  // step back into the questions and return to the preview without regenerating.
  const [generatedKey, setGeneratedKey] = React.useState<string | null>(null);

  const starterOverrides = React.useMemo(
    () => (vertical ? getSetupFunnelStarterOverrides(vertical, hasUpsell === true) : undefined),
    [vertical, hasUpsell],
  );
  const theme = vertical ? getSetupFunnelTheme(vertical) : 'normal';

  const { mutateAsync: updateFunnelWithRouting } = useUpdateFunnelWithRouting();
  const { mutate: persistVertical } = useUpdateStoreVertical();
  // Last vertical written to the store — guards against re-persisting the same value while the
  // merchant steps back and forth through the questions. Seeded from what the DB already holds.
  const lastPersistedVerticalRef = React.useRef<SetupFunnelVertical | null>(initialVertical);

  // When the merchant configured an upsell, bind the chosen offer onto the
  // generated funnel's offer step BEFORE the studio deploy runs. The deploy
  // merge spreads existing node config, so this binding survives — written in
  // the same shape the funnel editor persists (`stepConfig.resources.offer`).
  const prepareFunnel = React.useCallback(
    async (funnel: CreatedFunnel): Promise<CreatedFunnel> => {
      if (upsellOfferIds.length === 0) return funnel;
      const nodes = funnel.config?.nodes ?? [];
      let didBind = false;
      const nextNodes = nodes.map((node) => {
        if (node?.type !== 'offer') return node;
        didBind = true;
        return {
          ...node,
          config: {
            ...node.config,
            stepConfig: {
              ...node.config?.stepConfig,
              resources: {
                ...node.config?.stepConfig?.resources,
                offer: upsellOfferIds,
              },
            },
          },
        };
      });
      if (!didBind) return funnel;
      const nextConfig = { ...funnel.config, nodes: nextNodes };
      await updateFunnelWithRouting({ funnelId: funnel.id, storeId, config: nextConfig as any });
      return { ...funnel, config: nextConfig };
    },
    [upsellOfferIds, storeId, updateFunnelWithRouting],
  );

  const { orchestration, progressInfo, currentThumbnail, autoDeployFramePropsList, createFromTemplate, reset } =
    useFunnelCreation({ storeId, starterOverrides, prepareFunnel });

  const builderUrl = React.useMemo(
    () =>
      getNodeEditorConfig('checkout' as never, { hasStudioSource: true }).iframeUrl ||
      'http://localhost:3003',
    [],
  );

  // Dark verticals keep their authored dark palette — withholding storeBranding
  // prevents the deploy frame from repainting it with the store's brand color.
  const storeBranding = React.useMemo(
    () =>
      theme === 'dark'
        ? undefined
        : { primaryColor: storePrimaryColor || undefined, logoUrl: storeLogoUrl ?? undefined, storeName },
    [theme, storePrimaryColor, storeLogoUrl, storeName],
  );

  // Shopify/WooCommerce imports run as a background Inngest job, so the merchant can reach this
  // final step before (or just as) products land in the DB. Track the sync task to tell "still
  // importing" apart from a genuinely empty catalog, and poll the list while it runs.
  const syncTaskType: StoreTaskType | null =
    connectedProvider === 'shopify' || connectedProvider === 'woocommerce'
      ? PROVIDER_SYNC_TASK_TYPE[connectedProvider]
      : null;
  const syncTaskQuery = useLatestStoreTask(storeId, syncTaskType, { enabled: Boolean(storeId) });
  const syncStatus = syncTaskQuery.data?.status ?? null;
  const isSyncActive = syncStatus === 'pending' || syncStatus === 'processing';
  // Hold off on the "needs product" verdict until the first task-status read lands, so a 0-product
  // first paint doesn't flash the error before we know a sync is in flight.
  const isSyncStatusResolving = Boolean(syncTaskType) && syncTaskQuery.isLoading;

  const { data: productsData, isLoading: isLoadingProducts } = useProductsList(
    buildSetupProductsListInput(storeId, SETUP_CATALOG_LIST_PAGE_SIZE),
    {
      enabled: Boolean(storeId),
      // Poll the catalog while the import runs so the first product surfaces without waiting for
      // the whole sync — one sellable product is enough to complete setup.
      refetchInterval: isSyncActive ? SETUP_PRODUCT_POLL_INTERVAL_MS : false,
    },
  );
  const sellableProductCount = React.useMemo(
    () => buildCheckoutProductOptions(productsData?.items, baseCurrency).length,
    [baseCurrency, productsData?.items],
  );
  const hasSellableProduct = sellableProductCount > 0;
  // While we still have no usable product and a sync is running (or its status is still loading),
  // show an "importing" state instead of the error.
  const isImportingProducts = !hasSellableProduct && (isSyncActive || isSyncStatusResolving);

  // A finished sync guarantees the catalog is committed — pull the list once on completion so a
  // last batch that landed between polls is reflected immediately.
  const syncCompletionHandledRef = React.useRef(false);
  React.useEffect(() => {
    if (isSyncActive) {
      syncCompletionHandledRef.current = false;
      return;
    }
    if (syncStatus === 'completed' && !syncCompletionHandledRef.current) {
      syncCompletionHandledRef.current = true;
      void utils.products.list.invalidate();
    }
  }, [isSyncActive, syncStatus, utils]);

  // Advance to preview once the studio deploy pipeline finishes; fire onLaunched once.
  const launchedHandledRef = React.useRef(false);
  React.useEffect(() => {
    if (orchestration.phase === 'done' && phase === 'generating' && !launchedHandledRef.current) {
      launchedHandledRef.current = true;
      onLaunched();
      setPhase('preview');
    }
  }, [orchestration.phase, phase, onLaunched]);

  const effectiveFunnelId = orchestration.phase === 'done' ? orchestration.funnel.id : launchedFunnelId;

  // The preview's `/checkout/init` redirect needs the funnel's mounted checkout
  // URL as an override — without it the default host resolution is broken for
  // studio funnels. updateFunnelWithRouting persists it onto the checkout node,
  // so fetch the funnel once it exists and read `config.url`.
  const funnelQuery = useFunnel(effectiveFunnelId ?? '', storeId);
  const checkoutUrl = extractCheckoutUrlFromFunnel(funnelQuery.data);
  const isPreviewResolving = Boolean(effectiveFunnelId) && !checkoutUrl && funnelQuery.isLoading;

  // Opting into an upsell blocks completion until the offer step actually
  // carries the chosen offer (the binding lands via prepareFunnel and is
  // reflected once the funnel re-fetches).
  const isMissingUpsellBinding =
    hasUpsell === true &&
    upsellOfferIds.length > 0 &&
    Boolean(effectiveFunnelId) &&
    !funnelQuery.isFetching &&
    !offerNodeHasOfferBinding(funnelQuery.data, upsellOfferIds);
  const canComplete =
    Boolean(effectiveFunnelId) && hasSellableProduct && !isLoadingProducts && !isMissingUpsellBinding;

  const handleVerticalContinue = () => {
    // Fire-and-forget persist (must not block navigation); only write when the niche actually
    // changed from what's already stored, to avoid redundant writes + refetches.
    if (vertical && vertical !== lastPersistedVerticalRef.current) {
      persistVertical({ storeId, vertical });
      lastPersistedVerticalRef.current = vertical;
    }
    setPhase('upsell');
  };

  /** Generate the funnel from the current answers (vertical + upsell + offer). */
  const startGeneration = () => {
    if (!vertical || hasUpsell === null) return;
    const selectionKey = `${vertical}:${hasUpsell}:${upsellOfferIds.join(',')}`;
    // Unchanged answers + a funnel already generated → reuse it (no duplicate funnel).
    if (effectiveFunnelId && selectionKey === generatedKey) {
      setPhase('preview');
      return;
    }
    launchedHandledRef.current = false;
    setGeneratedKey(selectionKey);
    setPhase('generating');
    void createFromTemplate(getSetupFunnelTemplateId(hasUpsell));
  };

  const handleUpsellContinue = () => {
    if (hasUpsell === null) return;
    // Opting into an upsell requires choosing the offer product first.
    if (hasUpsell) {
      setPhase('upsell-offer');
      return;
    }
    // No upsell — drop any previously chosen offers and generate directly.
    setUpsellOfferIds([]);
    startGeneration();
  };

  const handleUpsellOfferContinue = () => {
    if (upsellOfferIds.length === 0) return;
    startGeneration();
  };

  const handleGenerateRetry = () => {
    reset();
    launchedHandledRef.current = false;
    setPhase(hasUpsell ? 'upsell-offer' : 'upsell');
  };

  const renderPhase = () => {
    switch (phase) {
      case 'vertical':
        return (
          <SetupCheckoutVerticalStep
            selected={vertical}
            onSelect={setVertical}
            onContinue={handleVerticalContinue}
            onBack={onBack}
            isDigital={isDigital}
          />
        );

      case 'upsell':
        return (
          <SetupCheckoutUpsellStep
            value={hasUpsell}
            onSelect={setHasUpsell}
            onContinue={handleUpsellContinue}
            onBack={() => setPhase('vertical')}
          />
        );

      case 'upsell-offer':
        return (
          <SetupCheckoutUpsellOfferStep
            storeId={storeId}
            selectedOfferIds={upsellOfferIds}
            onChange={setUpsellOfferIds}
            onContinue={handleUpsellOfferContinue}
            onBack={() => setPhase('upsell')}
          />
        );

      case 'generating':
        return (
          <SetupCheckoutGeneratingPanel
            builderUrl={builderUrl}
            storeBranding={storeBranding}
            frames={autoDeployFramePropsList}
            progressInfo={progressInfo}
            phase={orchestration.phase}
            currentThumbnail={currentThumbnail}
            errorMessage={orchestration.phase === 'error' ? orchestration.message : null}
            onRetry={handleGenerateRetry}
          />
        );

      case 'preview':
        return (
          <SetupStepSection
            title={t('experience.checkout.preview.title')}
            footer={
              <SetupStepFooter
                backLabel={t('footer.back')}
                // Step back into the checkout sub-flow (upsell offer when one was
                // chosen, else the upsell question) only when a funnel was generated
                // THIS session (generatedKey set). On a cold resume the funnel already
                // exists but the questions weren't answered this session — and a
                // pre-filled `vertical` must not route here, or continuing would
                // regenerate a duplicate funnel — so fall back to the payment step.
                onBack={generatedKey ? () => setPhase(hasUpsell ? 'upsell-offer' : 'upsell') : onBack}
                primaryLabel={t('experience.checkout.validateComplete')}
                onPrimary={onComplete}
                primaryDisabled={!canComplete}
              />
            }
          >
            <div className="flex flex-col gap-4">
              <p className="text-muted-foreground m-0 flex items-center gap-2 rounded-md bg-[color-mix(in_srgb,var(--primary)_7%,var(--background))] px-3 py-2.5 text-xs">
                <Paintbrush className="text-primary size-3.5 shrink-0" strokeWidth={2} aria-hidden />
                {t('experience.checkout.previewEditLaterHint')}
              </p>

              {isPreviewResolving ? (
                <div className="flex min-h-72 items-center justify-center">
                  <Loader2 className="text-muted-foreground size-6 animate-spin" aria-hidden />
                </div>
              ) : (
                <SetupCheckoutPreviewPanel
                  storeId={storeId}
                  baseCurrency={baseCurrency}
                  funnelId={effectiveFunnelId}
                  checkoutUrl={checkoutUrl}
                  isSyncing={isImportingProducts}
                />
              )}

              {!isLoadingProducts && !hasSellableProduct ? (
                isImportingProducts ? (
                  <p
                    className="text-muted-foreground m-0 flex items-center gap-2 rounded-md bg-[color-mix(in_srgb,var(--muted)_30%,var(--background))] px-3 py-2.5 text-xs"
                    role="status"
                    aria-live="polite"
                  >
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                    {t('experience.checkout.validateImporting')}
                  </p>
                ) : (
                  <p
                    className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
                    role="alert"
                  >
                    {t('experience.checkout.validateNeedsProduct')}
                  </p>
                )
              ) : null}

              {isMissingUpsellBinding ? (
                <p
                  className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
                  role="alert"
                >
                  {t('experience.checkout.validateNeedsUpsell')}
                </p>
              ) : null}
            </div>
          </SetupStepSection>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={phase}
        initial={CHECKOUT_PHASE_MOTION.initial}
        animate={CHECKOUT_PHASE_MOTION.animate}
        exit={CHECKOUT_PHASE_MOTION.exit}
        transition={CHECKOUT_PHASE_MOTION.transition}
        className="w-full"
      >
        {renderPhase()}
      </motion.div>
    </AnimatePresence>
  );
}
